import { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { formatCurrency as currency } from '../../utils/formatCurrency';
import { getDisplayPrice, getOrigPrice } from '../../utils/amountHelper';
import { ecomAddProduct, ecomRemoveProduct } from '../../utils/ecomHelper';
import API from '../../services/apiService';
import { CartContext } from '../../store/CartContext';
import parse from 'html-react-parser';

// ESlint complains about prop-types, you need to define it
import PropTypes from 'prop-types';
UpsellProctor.propTypes = {
  item: PropTypes.object.isRequired,
  prefix: PropTypes.string,
};

export default function UpsellProctor({ item, prefix = '' }) {
  const [cartState, cartDispatch] = useContext(CartContext);

  const proctorUpsells = useMemo(
    () =>
      item.upSellProducts
        .filter((upsell) => upsell.upsellSectionId === 7)
        .sort((a, b) => a.upsellOrderIndex - b.upsellOrderIndex),
    [item.upSellProducts]
  );

  const showProctor =
    (proctorUpsells.length > 0 && proctorUpsells.every((upsell) => !upsell.isSelected)) || proctorUpsells.length > 1;

  const isProctorValid = proctorUpsells.length === 0 || proctorUpsells.some((upsell) => upsell.isSelected);

  const proctorUpsellsProductIds = proctorUpsells.map((item) => item.productId);

  const currentUpsell = proctorUpsells.filter((upsell) => upsell.isSelected)[0];
  // console.log(currentUpsell?.cart_ProductId);

  const [selectedUpsell, setSelectedUpsell] = useState({});
  const [currentUpsellId, setCurrentUpsellId] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    setSelectedUpsell((state) => ({
      ...state,
      productId: currentUpsell?.productId || '00000000-0000-0000-0000-000000000000',
      prevProductId: null,
      cartProductId: currentUpsell?.cart_ProductId || '00000000-0000-0000-0000-000000000000',
      prevCartProductId: null,
    }));
  }, [currentUpsell]);

  // set validation error if no proctor upsell is selected
  useEffect(() => {
    // add error to error collection if no proctoring is selected
    const proctorKey = `proctor_${item.productId}`;
    if (!isProctorValid) {
      cartDispatch({
        type: 'VALIDATION',
        payload: {
          addErrors: {
            [proctorKey]: `Please select a <strong>Proctoring Option</strong> for <strong>${item.name}</strong>.`,
          },
        },
      });
    }
    // remove error from error collection if all fields are valid
    else {
      cartDispatch({
        type: 'VALIDATION',
        payload: {
          removeErrors: [proctorKey],
        },
      });
    }
  }, [cartDispatch, isProctorValid, item]);

  const storeCart = (cartData) => {
    cartDispatch({
      type: 'UPDATE',
      payload: {
        ...cartData,
        isLoading: false,
        isUpdatingCart: false,
      },
    });

    // trigger to show the toast notification
    cartDispatch({
      type: 'EXPERIENCE',
      payload: {
        showNotification: true,
      },
    });
  };

  const storeCartProducts = (updatedCartProducts) => {
    cartDispatch({
      type: 'UPDATE',
      payload: {
        cartProducts: updatedCartProducts,
        isLoading: true,
        isUpdatingCart: true,
      },
    });
  };

  const upsellDebounce = useRef();

  const handleChange = (event) => {
    const { value, type, checked, dataset } = event.target;
    const theValue = type === 'checkbox' ? checked : value;

    setIsUpdating(true);
    setCurrentUpsellId(value);

    // trigger to hide the toast notification
    cartDispatch({
      type: 'EXPERIENCE',
      payload: {
        showNotification: false,
      },
    });

    const selectedProductId = theValue;
    const selectedCartProductId = dataset.cartProductId;
    const prevProductId = selectedUpsell.productId;
    const prevCartProductId = selectedUpsell.cartProductId;

    // console.log(prevProductId);
    // console.log(selectedProductId);

    setSelectedUpsell(() => ({
      productId: selectedProductId,
      prevProductId: prevProductId,
      cartProductId: selectedCartProductId,
      prevCartProductId: prevCartProductId,
    }));

    // if isSelected remove the other selected proctor upsell
    const updatedCartProducts = cartState?.cartProducts?.map((product) => {
      if (product.productId === item.productId) {
        const upsells = product.upSellProducts;
        const updatedUpsells = upsells.map((productUpsell) => {
          // removes previusly selected proctor upsell
          if (proctorUpsellsProductIds.includes(productUpsell.productId)) {
            return {
              ...productUpsell,
              isSelected: productUpsell.productId === selectedProductId,
            };
          }
          return productUpsell;
        });

        return {
          ...product,
          upSellProducts: updatedUpsells,
        };
      }
      return product;
    });

    storeCartProducts(updatedCartProducts);

    // debouncing API call in case the user repeated clicks the upsell option
    clearTimeout(upsellDebounce.current);
    upsellDebounce.current = setTimeout(() => {
      const upsellData = {
        cartProductId: selectedCartProductId, // upsell cart_ProductId
        cartId: item.cartId, // cartId
        productId: selectedProductId,
        quantity: item.quantity, // use quantity of main product or you can leave 1, it will automatically use the main product's quantity
        isSelected: true, // true/false, add or remove upsell
      };

      const prevUpsellData = {
        cartProductId: prevCartProductId, // upsell cart_ProductId
        cartId: item.cartId, // cartId
        productId: prevProductId,
        quantity: item.quantity, // use quantity of main product or you can leave 1, it will automatically use the main product's quantity
        isSelected: false, // true/false, add or remove upsell
      };

      const upsellForAdd = proctorUpsells.filter((upsell) => upsell.productId === upsellData.productId)[0];
      const upsellForRemoval = proctorUpsells.filter((upsell) => upsell.productId === prevUpsellData.productId)[0];

      if (selectedProductId === '00000000-0000-0000-0000-000000000000') {
        API.updateUpsell(prevUpsellData).then((res) => {
          // console.log('cart', res.data);
          const cart = res.data;

          setIsUpdating(false);

          storeCart(cart);

          // push event and data to dataLayer
          ecomRemoveProduct(upsellForRemoval);
        });
      } else {
        API.updateUpsell(upsellData)
          .then((res) => {
            // console.log('cart', res.data);
            const cart = res.data;

            // push event and data to dataLayer
            ecomAddProduct(upsellForAdd);

            return cart;
          })
          .then((cartData) => {
            if (prevProductId !== '00000000-0000-0000-0000-000000000000') {
              API.updateUpsell(prevUpsellData).then((res) => {
                // console.log('cart', res.data);
                const cart = res.data;
                setIsUpdating(false);
                storeCart(cart);

                // push event and data to dataLayer
                ecomRemoveProduct(upsellForRemoval);
              });
            } else {
              setIsUpdating(false);
              storeCart(cartData);
            }
          })
          .catch((err) => {
            console.error(err?.response?.data);

            // show error message or reload the page
            cartDispatch({
              type: 'ERROR',
              payload: {
                isLoading: false,
                isError: true,
                error: err?.response?.data || 'General Error',
              },
            });

            if (err?.response?.data?.status >= 400) {
              console.error('Upsell was not updated!');
            }
          });
      }
    }, 300);
  };

  const showUpsellDescription = (upsell) => {
    console.log(upsell);
    cartDispatch({
      type: 'EXPERIENCE',
      payload: {
        showUpsellDescription: {
          name: upsell.name,
          description: upsell.description,
        },
      },
    });
  };

  const proctorUpsellRadios = proctorUpsells.map((upsell) => {
    const dispayPrice = getDisplayPrice(upsell, true);
    const origPrice = getOrigPrice(upsell, true);

    return (
      <div className="form-check" key={upsell.cart_ProductId}>
        {isUpdating && upsell.productId === currentUpsellId ? (
          <span className="spinner-border spinner-border-sm upsell-check" role="status" aria-hidden="true"></span>
        ) : (
          <input
            className="form-check-input"
            type="radio"
            name="digital"
            value={upsell.productId}
            id={`${prefix}upsell-cart-id-${upsell.cart_ProductId}`}
            checked={upsell.productId === selectedUpsell.productId}
            onChange={handleChange}
            data-cart-product-id={upsell.cart_ProductId}
            data-upsell-type={upsell.upsellType}
          />
        )}
        <div className="d-flex justify-content-between">
          <label className="form-check-label d-block" htmlFor={`${prefix}upsell-cart-id-${upsell.cart_ProductId}`}>
            <span className="fw-600 d-block d-sm-flex">
              <span className="upsell-name d-inline-block">
                {upsell.name}{' '}
                <span className="d-inline-block">
                  <span className="upsell-price d-sm-none text-end">
                    <span className="d-sm-none">(</span>
                    {upsell.discountSource && upsell.discountSource !== 'BuiltIn' && (
                      <span className="orig-price me-2">+{currency(origPrice)}</span>
                    )}
                    +{currency(dispayPrice)}
                    <span className="d-sm-none">)</span>
                  </span>{' '}
                  <button
                    className="btn-toggle upsell-description-toggle fw-400 small text-reset lh-1 p-0 me-1"
                    type="button"
                    onClick={() => showUpsellDescription(upsell)}
                  >
                    <span className="info-icon align-middle">?</span>
                  </button>
                </span>
              </span>
            </span>
          </label>
          <span className="upsell-price d-none d-sm-inline-block text-end fw-600">
            <span className="d-sm-none">(</span>
            {upsell.discountSource && upsell.discountSource !== 'BuiltIn' && (
              <span className="orig-price me-2">+{currency(origPrice)}</span>
            )}
            +{currency(dispayPrice)}
            <span className="d-sm-none">)</span>
          </span>
        </div>

        {/* <label className="form-check-label d-block" htmlFor={`${prefix}upsell-cart-id-${upsell.cart_ProductId}`}>
          <div className="fw-600 d-block d-sm-flex justify-content-between mb-0">
            <span className="upsell-name">{upsell.name}</span>{' '}
            <button
              className="btn-toggle fw-400 small text-reset lh-1 p-0 me-1"
              type="button"
              onClick={() => showUpsellDescription(upsell)}
            >
              <span className="info-icon align-middle">?</span>
            </button>
            <span className="upsell-price d-inline-block text-end">
              <span className="d-sm-none">(</span>
              {upsell.discountSource && upsell.discountSource !== 'BuiltIn' && (
                <span className="orig-price me-2">+{currency(origPrice)}</span>
              )}
              +{currency(dispayPrice)}
              <span className="d-sm-none">)</span>
            </span>
          </div>
        </label> */}
      </div>
    );
  });

  if (!showProctor) {
    return null;
  }

  return (
    <div className="upsell-group proctor-group">
      <h3 className="fs-5 fw-400 mt-4 mb-2">Select Proctoring Option:</h3>
      <div className={`upsell-item rounded is-selected ${cartState.isLoading ? 'is-loading' : ''}`}>
        <form className={`${!isProctorValid && cartState.validation.formTouched ? 'no-proctor-selected' : ''}`}>
          {proctorUpsellRadios}
        </form>
      </div>
    </div>
  );
}
